import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { app } from '../../firebase'; 

import NavbarDashboard from '../Dashboard/NavbarDashboard';
import { Box, Button } from '@mui/material';
import ResponsiveDrawer from '../Dashboard/Sidebar';
import { Refresh as RefreshIcon, Download as DownloadIcon } from '@mui/icons-material';

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const firestore = getFirestore(app);

const UserStatus = ({ userPermissions }) => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const getTimestampFromUser = (user) => {
    if (user.timestamp) {
      return new Date(user.timestamp);
    }
    if (user.createdAt?.seconds) {
      return new Date(user.createdAt.seconds * 1000);
    }
    if (user.createdAt) {
      return new Date(user.createdAt);
    }
    return new Date(0);
  };

  const formatDate = (user) => {
    const date = getTimestampFromUser(user);
    return Number.isNaN(date.getTime()) ? 'NA' : date.toLocaleString();
  };

  const fetchData = async () => {
    try {
      const [usersSnapshot, tdsStateSnapshot, tdsFormSnapshot] = await Promise.all([
        getDocs(collection(firestore, 'users')),
        getDocs(collection(firestore, 'tds_state')),
        getDocs(collection(firestore, 'tds_forms'))
      ]);

      const usersData = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const tdsStateMap = new Map(
        tdsStateSnapshot.docs.map(doc => [doc.id, doc.data()])
      );
      const tdsFormMap = new Map(
        tdsFormSnapshot.docs.map(doc => {
          const data = doc.data();
          return [
            data.userId ?? doc.id,
            {
              ...data,
              timestamp: data.timestamp?.toDate() ?? null
            }
          ];
        })
      );

      const mergedData = usersData.map(user => {
        const tdsState = tdsStateMap.get(user.id) || {};
        const tdsForm = tdsFormMap.get(user.id) || {};
        
        // Omit email/phone from tdsForm to prevent overwrite of registration credentials
        const { email: formEmail, phone: formPhone, ...restTdsForm } = tdsForm;
        
        return { ...user, ...restTdsForm, ...tdsState };
      });

      const sortedData = mergedData.sort((a, b) => {
        const timeA = getTimestampFromUser(a).getTime();
        const timeB = getTimestampFromUser(b).getTime();
        return timeB - timeA;
      });

      setUsers(sortedData);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };
  

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 600000); 
    return () => clearInterval(interval);
  }, []);

  const filteredUsers = users.filter(
    user =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.phone && user.phone.includes(searchTerm))
  );
  console.log('Merged and Sorted Data:', filteredUsers); // Debugging log to check merged and sorted data

  // 📥 Download Excel Function
  const downloadExcel = () => {
    const worksheetData = filteredUsers.map((user, index) => ({
      "Sr No.": index + 1,
      "Date & Time": formatDate(user),
      "User Name": user.name || "",
      "Email ID": user.email || "",
      "Phone No.": user.phone || "",
      "TDS Form Status": user.tdsformfilluped ? "Complete" : "Incomplete",
      "Income Tax Website Registration": user.incomesiteregistration ? "Complete" : "Incomplete",
      "ITR File Status": user.fileTds ? "Complete" : "Incomplete",
      "26 AS Upload": user.tdsA26s ? "Complete" : "Incomplete",
      "Payment Status": user.paymentstatus ? "Complete" : "Incomplete",
      "E-verification": user.finalotpstatus ? "Complete" : "Incomplete",
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "User Report");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "User_Report.xlsx");
  };

  return (
    <div>
      <NavbarDashboard />
      <Box height={10} />
      <Box sx={{ display: 'flex' }}>
        <ResponsiveDrawer userPermissions={userPermissions} />
        <Box component="main" sx={{ flexGrow: 1, p: 9, minWidth: 0 }}>
          <div className="container">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search by username or email or phone number..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <Button
                variant="contained"
                onClick={fetchData}
                style={{ backgroundColor: 'lightgreen', marginLeft: '10px' }}
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>

              {/* 📥 Download Report Button */}
              <Button
                variant="contained"
                onClick={downloadExcel}
                style={{ backgroundColor: '#2196F3', marginLeft: '10px', color: 'white' }}
                startIcon={<DownloadIcon />}
              >
                Download Report
              </Button>
            </div>

            <div className="table-wrapper">
              <table className="responsive-table">
                <thead>
                  <tr>
                    <th className="sticky-header">Sr No.</th>
                    <th className="sticky-header">Date & Time</th>
                    <th className="sticky-header">User Name</th>
                    <th className="sticky-header">Email ID</th>
                    <th className="sticky-header">Phone No.</th>
                    <th className="sticky-header">TDS Form Status</th>
                    <th className="sticky-header">Income Tax Website Registration Status</th>
                    <th className="sticky-header">ITR File Status</th>
                    <th className="sticky-header">26 As Upload</th>
                    <th className="sticky-header">Payment Status</th>
                    <th className="sticky-header">E-verification</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, index) => (
                    <tr key={user.id} className={index % 2 === 0 ? 'even' : 'odd'}>
                      <td>{index + 1}</td>
                      <td>{formatDate(user)}</td>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.phone}</td>
                      <td style={{ backgroundColor: user.tdsformfilluped ? '#03C03C' : '#EF0107', color: 'white' }}>
                        {user.tdsformfilluped ? 'Complete' : 'Incomplete'}
                      </td>
                      <td style={{ backgroundColor: user.incomesiteregistration ? '#03C03C' : '#EF0107', color: 'white' }}>
                        {user.incomesiteregistration ? 'Complete' : 'Incomplete'}
                      </td>
                      <td style={{ backgroundColor: user.fileTds ? '#03C03C' : '#EF0107', color: 'white' }}>
                        {user.fileTds ? 'Complete' : 'Incomplete'}
                      </td>
                      <td style={{ backgroundColor: user.tdsA26s ? '#03C03C' : '#EF0107', color: 'white' }}>
                        {user.tdsA26s ? 'Complete' : 'Incomplete'}
                      </td>
                      <td style={{ backgroundColor: user.paymentstatus ? '#03C03C' : '#EF0107', color: 'white' }}>
                        {user.paymentstatus ? 'Complete' : 'Incomplete'}
                      </td>
                      <td style={{ backgroundColor: user.finalotpstatus ? '#03C03C' : '#EF0107', color: 'white' }}>
                        {user.finalotpstatus ? 'Complete' : 'Incomplete'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Box>
      </Box>
    </div>
  );
};

export default UserStatus;

// CSS
const css = `
.even {
  background-color: #f2f2f2;
}
.odd {
  background-color: #ffffff;
}
.sticky-header {
  position: sticky;
  top: 0;
  background-color: lightgreen;
  z-index: 10;
  box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.4);
}
.table-wrapper {
  overflow-x: auto;
  max-height: calc(100vh - 300px);
}
.responsive-table {
  width: 100%;
  border-collapse: collapse;
}
.responsive-table th,
.responsive-table td {
  padding: 12px;
  text-align: left;
  border: 1px solid #ddd;
}
.responsive-table th {
  font-weight: bold;
}
.table-row.even {
  background-color: #f2f2f2;
}
.table-row.odd {
  background-color: #ffffff;
}
`;

document.head.insertAdjacentHTML('beforeend', `<style>${css}</style>`);
